import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff,
  Save,
  X,
  Users,
  Search,
  Filter,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const AdminEventsPage = () => {
  const { user, token, isReadOnly } = useAuth();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsPerPage] = useState(6);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    category: 'messe',
    max_participants: '',
    registration_required: false,
    organizer: '',
    status: 'published'
  });

  const categories = [
    { value: 'messe', label: 'Messe', icon: '💒', color: 'bg-blue-100 text-blue-800' },
    { value: 'retraite', label: 'Retraite', icon: '🙏', color: 'bg-purple-100 text-purple-800' },
    { value: 'formation', label: 'Formation', icon: '📚', color: 'bg-green-100 text-green-800' },
    { value: 'pelerinage', label: 'Pèlerinage', icon: '✈️', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'celebration', label: 'Célébration', icon: '🎉', color: 'bg-pink-100 text-pink-800' },
    { value: 'rencontre', label: 'Rencontre', icon: '🤝', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'adoration', label: 'Adoration', icon: '⛪', color: 'bg-red-100 text-red-800' }
  ];

  const statuses = [
    { value: 'draft', label: 'Brouillon', color: 'bg-gray-100 text-gray-800' },
    { value: 'published', label: 'Publié', color: 'bg-green-100 text-green-800' },
    { value: 'cancelled', label: 'Annulé', color: 'bg-red-100 text-red-800' }
  ];

  // Check if user has admin rights
  const hasAdminRights = () => {
    return user && ['admin', 'apd', 'apz', 'moderateur', 'tgd', 'tgz', 'mcd', 'mcz'].includes(user.role);
  };

  useEffect(() => {
    const userHasAdminRights = user && ['admin', 'apd', 'apz', 'moderateur', 'tgd', 'tgz', 'mcd', 'mcz'].includes(user.role);
    if (userHasAdminRights && token) {
      fetchEvents();
    }
  }, [user, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterEvents();
  }, [events, searchTerm, filterCategory, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = async () => {
    console.log('🔍 fetchEvents called - user:', user, 'token:', token ? 'exists' : 'missing');
    
    if (!token) {
      console.error('❌ No token available');
      showNotification('Veuillez vous connecter pour accéder à cette page', 'error');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('📡 Making API request to /api/events');
      
      const response = await fetch('http://localhost:8000/api/events', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      console.log('📥 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Events data received:', data);
        setEvents(data.data || data);
        showNotification(`${(data.data || data).length} événements chargés`, 'success');
      } else {
        const errorData = await response.text();
        console.error('❌ API Error:', response.status, errorData);
        showNotification(`Erreur ${response.status}: ${errorData}`, 'error');
      }
    } catch (error) {
      console.error('❌ Network Error:', error);
      showNotification('Erreur de connexion au serveur', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterEvents = () => {
    let filtered = events;

    if (searchTerm) {
      filtered = filtered.filter(event => 
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterCategory !== 'all') {
      filtered = filtered.filter(event => event.category === filterCategory);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(event => event.status === filterStatus);
    }

    setFilteredEvents(filtered);
    setCurrentPage(1); // Reset pagination when filters change
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    try {
      const url = editingEvent 
        ? `http://localhost:8000/api/events/${editingEvent.id}`
        : 'http://localhost:8000/api/events';
      
      const method = editingEvent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        showNotification(
          editingEvent ? 'Événement modifié avec succès' : 'Événement créé avec succès', 
          'success'
        );
        fetchEvents();
        resetForm();
      } else {
        const errorData = await response.json();
        showNotification(errorData.message || 'Erreur lors de l\'enregistrement', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur de connexion', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      date: event.date,
      time: event.time,
      location: event.location,
      category: event.category,
      max_participants: event.max_participants || '',
      registration_required: event.registration_required || false,
      organizer: event.organizer || '',
      status: event.status
    });
    setShowForm(true);
  };

  const handleDelete = async (eventId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
      try {
        const response = await fetch(`http://localhost:8000/api/events/${eventId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });

        if (response.ok) {
          showNotification('Événement supprimé avec succès', 'success');
          fetchEvents();
        } else {
          showNotification('Erreur lors de la suppression', 'error');
        }
      } catch (error) {
        console.error('Erreur:', error);
        showNotification('Erreur de connexion', 'error');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      date: '',
      time: '',
      location: '',
      category: 'messe',
      max_participants: '',
      registration_required: false,
      organizer: '',
      status: 'published'
    });
    setEditingEvent(null);
    setShowForm(false);
  };

  const getCategoryInfo = (category) => {
    return categories.find(cat => cat.value === category) || categories[0];
  };

  const getStatusInfo = (status) => {
    return statuses.find(stat => stat.value === status) || statuses[0];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Pagination logic
  const getCurrentEvents = () => {
    const indexOfLastEvent = currentPage * eventsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
    return filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);
  };

  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Reset pagination when filters change
  const resetPagination = () => {
    setCurrentPage(1);
  };

  // Debug info
  console.log('🔍 AdminEventsPage render - user:', user, 'token:', token ? 'exists' : 'missing', 'hasAdminRights:', hasAdminRights());

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-center mb-2">Connexion requise</h2>
          <p className="text-gray-600 text-center">Veuillez vous connecter pour accéder à cette page.</p>
          <div className="mt-4 text-center">
            <a href="/login" className="text-blue-500 hover:text-blue-700 underline">
              Se connecter
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAdminRights()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-center mb-2">Accès non autorisé</h2>
          <p className="text-gray-600 text-center">Vous n'avez pas les droits pour accéder à cette page.</p>
          <p className="text-gray-500 text-center text-sm mt-2">Rôle actuel: {user?.role}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Main Header - Style identique à MembersPage */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 space-y-4 sm:space-y-0">
            {/* Breadcrumb Navigation */}
            <nav className="flex items-center space-x-2 text-sm">
              <button 
                onClick={() => window.history.back()}
                className="flex items-center text-gray-500 hover:text-purple-600 transition-colors"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="hidden sm:inline">Retour au site</span>
                <span className="sm:hidden">Retour</span>
              </button>
              <span className="text-gray-300">/</span>
              <button 
                onClick={() => window.location.href = '/dashboard'}
                className="text-gray-500 hover:text-purple-600 transition-colors"
              >
                <span className="hidden sm:inline">Dashboard</span>
                <span className="sm:hidden">Admin</span>
              </button>
              <span className="text-gray-300">/</span>
              <span className="text-purple-600 font-medium">
                <span className="hidden sm:inline">Gestion des Événements</span>
                <span className="sm:hidden">Événements</span>
              </span>
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto">
              {/* Refresh Button */}
              <button 
                onClick={() => {
                  fetchEvents();
                  showNotification('Données actualisées', 'success');
                }}
                className="flex items-center px-3 py-2 text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors text-sm"
                title="Actualiser"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline">Actualiser</span>
                <span className="sm:hidden">MAJ</span>
              </button>

              {/* User Info */}
              <span className="text-gray-700 font-medium text-sm hidden md:inline">
                {user?.first_name} {user?.last_name}
              </span>

              {/* Admin Badge */}
              <div className="flex items-center">
                <span className="bg-purple-600 text-white px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium">
                  {user?.role}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Page Title and Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              <span className="hidden sm:inline">Gestion des Événements</span>
              <span className="sm:hidden">Événements</span>
            </h1>
            <p className="text-gray-600 mt-2 text-justify text-sm sm:text-base">
              <span className="hidden sm:inline">Créez et gérez les événements de la communauté FSF</span>
              <span className="sm:hidden">Gérez les événements FSF</span>
            </p>
          </div>
          {!isReadOnly && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 sm:px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors shadow-lg w-full sm:w-auto justify-center"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">
              <span className="hidden sm:inline">Nouvel Événement</span>
              <span className="sm:hidden">Nouveau</span>
            </span>
          </button>
          )}
        </div>

        {/* Notification */}
        {notification.show && (
          <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 ${
            notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span>{notification.message}</span>
          </div>
        )}

    {/* Statistics */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-8 rounded-xl shadow-lg border border-blue-200">
            <div className="flex items-center">
              <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg">
                <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="ml-4 sm:ml-6">
                <p className="text-xs sm:text-sm font-semibold text-blue-700 uppercase tracking-wide">
                  <span className="hidden sm:inline">Total événements</span>
                  <span className="sm:hidden">Total</span>
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-blue-900">{events.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-4 sm:p-8 rounded-xl shadow-lg border border-green-200">
            <div className="flex items-center">
              <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 shadow-lg">
                <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="ml-4 sm:ml-6">
                <p className="text-xs sm:text-sm font-semibold text-green-700 uppercase tracking-wide">
                  <span className="hidden sm:inline">Événements publiés</span>
                  <span className="sm:hidden">Publiés</span>
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-green-900">
                  {events.filter(e => e.status === 'published').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-yellow-50 to-orange-100 p-4 sm:p-8 rounded-xl shadow-lg border border-yellow-200 sm:col-span-2 lg:col-span-1">
            <div className="flex items-center">
              <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-r from-yellow-600 to-orange-600 shadow-lg">
                <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="ml-4 sm:ml-6">
                <p className="text-xs sm:text-sm font-semibold text-yellow-700 uppercase tracking-wide">
                  <span className="hidden sm:inline">Événements à venir</span>
                  <span className="sm:hidden">À venir</span>
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-yellow-900">
                  {events.filter(e => new Date(e.date) >= new Date()).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 mb-8 border border-gray-100">
          <div className="flex items-center mb-4 sm:mb-6">
            <Filter className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 mr-2 sm:mr-3" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              <span className="hidden sm:inline">Filtres et Recherche</span>
              <span className="sm:hidden">Recherche</span>
            </h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Search Input */}
            <div className="relative group lg:col-span-3 xl:col-span-1">
              <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 transition-colors group-focus-within:text-indigo-600" />
              <input
                type="text"
                placeholder="Rechercher un événement..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 sm:pl-12 pr-4 py-3 sm:py-4 w-full border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white text-sm sm:text-base"
              />
            </div>
            
            {/* Category Filter */}
            <div className="relative">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="py-3 sm:py-4 px-4 sm:px-6 w-full border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white appearance-none cursor-pointer text-sm sm:text-base"
              >
                <option value="all">🏷️ Toutes les catégories</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.icon} {cat.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            
            {/* Status Filter */}
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="py-3 sm:py-4 px-4 sm:px-6 w-full border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white appearance-none cursor-pointer text-sm sm:text-base"
              >
                <option value="all">📊 Tous les statuts</option>
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
          
          {/* Filter Summary */}
          {(searchTerm || filterCategory !== 'all' || filterStatus !== 'all') && (
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
              <div className="flex flex-col sm:flex-row justify-between space-y-2 sm:space-y-0">
                <div className="flex flex-wrap items-center gap-2 text-sm text-indigo-700">
                  <span className="font-medium">Filtres actifs:</span>
                  {searchTerm && <span className="bg-indigo-200 px-2 py-1 rounded text-xs">🔍 "{searchTerm}"</span>}
                  {filterCategory !== 'all' && <span className="bg-purple-200 px-2 py-1 rounded text-xs">🏷️ {categories.find(c => c.value === filterCategory)?.label}</span>}
                  {filterStatus !== 'all' && <span className="bg-blue-200 px-2 py-1 rounded text-xs">📊 {statuses.find(s => s.value === filterStatus)?.label}</span>}
                </div>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterCategory('all');
                    setFilterStatus('all');
                  }}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium self-start sm:self-center"
                >
                  Effacer tout
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6">
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {editingEvent ? 'Modifier l\'événement' : 'Nouvel événement'}
                  </h2>
                  <button
                    onClick={resetForm}
                    className="text-gray-400 hover:text-gray-600 p-2"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Titre de l'événement
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        placeholder="Ex: Messe des familles FSF"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date
                        </label>
                        <input
                          type="date"
                          required
                          value={formData.date}
                          onChange={(e) => setFormData({...formData, date: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Heure
                        </label>
                        <input
                          type="time"
                          value={formData.time}
                          onChange={(e) => setFormData({...formData, time: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Lieu
                        </label>
                        <input
                          type="text"
                          value={formData.location}
                          onChange={(e) => setFormData({...formData, location: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                          placeholder="Ex: Église Notre-Dame"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Catégorie
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({...formData, category: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        >
                          {categories.map(cat => (
                            <option key={cat.value} value={cat.value}>
                              {cat.icon} {cat.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Organisateur
                        </label>
                        <input
                          type="text"
                          value={formData.organizer}
                          onChange={(e) => setFormData({...formData, organizer: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                          placeholder="Nom de l'organisateur"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <span className="hidden sm:inline">Nombre max de participants</span>
                          <span className="sm:hidden">Max participants</span>
                        </label>
                        <input
                          type="number"
                          value={formData.max_participants}
                          onChange={(e) => setFormData({...formData, max_participants: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                          placeholder="Laisser vide si illimité"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Statut
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                      >
                        {statuses.map(status => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        placeholder="Description détaillée de l'événement..."
                      />
                    </div>

                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.registration_required}
                          onChange={(e) => setFormData({...formData, registration_required: e.target.checked})}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Inscription requise
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-4 sm:pt-6 border-t">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="w-full sm:w-auto px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full sm:w-auto px-6 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                        isSubmitting 
                          ? 'bg-blue-400 cursor-not-allowed' 
                          : 'bg-blue-600 hover:bg-blue-700'
                      } text-white`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                          <span className="hidden sm:inline">Enregistrement...</span>
                          <span className="sm:hidden">...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          <span>{editingEvent ? 'Modifier' : 'Créer'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Events List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          {loading ? (
            <div className="text-center py-16">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600 mx-auto"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Calendar className="w-8 h-8 text-indigo-600" />
                </div>
              </div>
              <p className="text-gray-600 mt-6 text-lg">Chargement des événements...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-16">
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucun événement trouvé</h3>
              <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6">
              {getCurrentEvents().map((event) => {
                const categoryInfo = getCategoryInfo(event.category);
                const statusInfo = getStatusInfo(event.status);
                
                return (
                  <div key={event.id} className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-4 sm:p-6 border-2 border-gray-100 hover:border-indigo-200 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    {/* Event Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start mb-4 space-y-2 sm:space-y-0">
                      <div className="flex flex-wrap items-center gap-2 text-justify w-full sm:w-auto">
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold ${categoryInfo.color} shadow-sm`}>
                          {categoryInfo.icon} <span className="hidden sm:inline">{categoryInfo.label}</span>
                        </span>
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.color} shadow-sm`}>
                          {statusInfo.label}
                        </span>
                        {event.registration_required && (
                          <span className="px-2 sm:px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-orange-100 to-red-100 text-orange-800 shadow-sm">
                            📝 <span className="hidden sm:inline">Inscription requise</span>
                          </span>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      {!isReadOnly && (
                      <div className="flex items-center space-x-2 self-end sm:self-start">
                        <button
                          onClick={() => handleEdit(event)}
                          className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-all duration-200 hover:scale-110"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(event.id)}
                          className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-all duration-200 hover:scale-110"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      )}
                    </div>
                    
                    {/* Event Title */}
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 line-clamp-2 text-justify">
                      {event.title}
                    </h3>
                    
                    {/* Event Details */}
                    <div className="grid grid-cols-1 gap-2 sm:gap-3 mb-4">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 text-sm">
                        <div className="flex items-center space-x-2 text-indigo-600">
                          <Calendar className="w-4 h-4 flex-shrink-0" />
                          <span className="font-medium text-xs sm:text-sm">{formatDate(event.date)}</span>
                        </div>
                        {event.time && (
                          <div className="flex items-center space-x-2 text-purple-600">
                            <Clock className="w-4 h-4 flex-shrink-0" />
                            <span className="font-medium text-xs sm:text-sm">{event.time}</span>
                          </div>
                        )}
                      </div>
                      {event.location && (
                        <div className="flex items-center space-x-2 text-sm text-green-600">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="font-medium text-xs sm:text-sm truncate">{event.location}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Event Description */}
                    {event.description && (
                      <p className="text-gray-700 text-sm mb-4 line-clamp-3 text-justify">
                        {event.description}
                      </p>
                    )}
                    
                    {/* Event Meta */}
                    <div className="flex flex-col sm:flex-row justify-between text-xs text-gray-500 pt-3 border-t border-gray-200 space-y-2 sm:space-y-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        {event.organizer && (
                          <span className="flex items-center space-x-1">
                            <Users className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{event.organizer}</span>
                          </span>
                        )}
                        {event.max_participants && (
                          <span className="bg-gray-100 px-2 py-1 rounded whitespace-nowrap">
                            Max: {event.max_participants}
                          </span>
                        )}
                      </div>
                      <span className="text-gray-400 self-end sm:self-center">
                        ID: {event.id}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredEvents.length > eventsPerPage && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mt-8">
            <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row justify-between items-center">
              <div className="text-sm text-gray-700 order-2 sm:order-1">
                <span className="hidden sm:inline">
                  Affichage de {((currentPage - 1) * eventsPerPage) + 1} à {Math.min(currentPage * eventsPerPage, filteredEvents.length)} sur {filteredEvents.length} événements
                </span>
                <span className="sm:hidden">
                  {((currentPage - 1) * eventsPerPage) + 1}-{Math.min(currentPage * eventsPerPage, filteredEvents.length)} sur {filteredEvents.length}
                </span>
              </div>
              
              <div className="flex items-center space-x-2 order-1 sm:order-2">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600 transform hover:scale-105'
                  }`}
                >
                  <span className="hidden sm:inline">Précédent</span>
                  <span className="sm:hidden">«</span>
                </button>

                {/* Page Numbers - Desktop */}
                <div className="hidden sm:flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, index) => {
                    const pageNumber = index + 1;
                    const isCurrentPage = pageNumber === currentPage;
                    const shouldShow = 
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);

                    if (!shouldShow) {
                      if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                        return <span key={pageNumber} className="px-2 text-gray-400">...</span>;
                      }
                      return null;
                    }

                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`w-10 h-10 rounded-lg font-medium transition-all duration-200 ${
                          isCurrentPage
                            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg transform scale-110'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:transform hover:scale-105'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                {/* Page Numbers - Mobile */}
                <div className="flex sm:hidden items-center space-x-1 text-sm">
                  <span className="bg-gray-100 px-3 py-2 rounded-lg">
                    {currentPage} / {totalPages}
                  </span>
                </div>

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600 transform hover:scale-105'
                  }`}
                >
                  <span className="hidden sm:inline">Suivant</span>
                  <span className="sm:hidden">»</span>
                </button>
              </div>
            </div>
          </div>
        )}

        
       
      </div>
    </div>
  );
};

export default AdminEventsPage;